import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useAuthDialog } from "@/lib/AuthDialogContext";
import DarkModeToggle from "./DarkModeToggle";
import GlobalSearch from "./GlobalSearch";
import NotificationsBell from "./NotificationsBell";
import { Button } from "@/components/ui/button";
import { Sparkles, Search, LogOut, User as UserIcon, Menu, X } from "lucide-react";

export default function Navbar({ onSignIn }) {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const { open: openAuth } = useAuthDialog();
  const navigate = useNavigate();

  const triggerSignIn = () => (onSignIn ? onSignIn() : openAuth("login"));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const goDashboard = () => {
    if (!user) return triggerSignIn();
    if (!user.role) navigate("/onboarding");
    else navigate("/student/dashboard");
  };

  const links = [
    { to: "/", label: "Home" },
    { to: "/browse/notes", label: "Notes" },
    { to: "/browse/learnings", label: "Learnings" },
    { to: "/learn/quizzes", label: "Learn" },
  ];

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
          scrolled ? "glass-strong py-3 border-b border-border" : "bg-transparent py-5"
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
            <div className="relative w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-lg">
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-xl pointer-events-none"
                style={{
                  boxShadow: "0 0 24px rgba(103,232,249,0.6)",
                  animation: "logo-pulse 6s ease-in-out infinite",
                }}
              />
              <Sparkles className="w-4 h-4 text-white relative" strokeWidth={2.2} />
            </div>
            <div className="hidden sm:block">
              <div className="font-display text-xl font-black tracking-tight leading-none">Luminon</div>
              <div className="text-[9px] uppercase tracking-widest text-muted-foreground leading-none mt-0.5">Building brighter minds</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground/70 hover:text-foreground hover:bg-muted transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setSearchOpen(true)}
              data-testid="navbar-search-btn"
              className="hidden md:flex items-center gap-2 px-3 h-10 rounded-full glass text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <Search className="w-4 h-4" strokeWidth={1.8} />
              <span>Search…</span>
              <kbd className="hidden md:inline text-[10px] px-1.5 py-0.5 rounded bg-muted border border-border">⌘K</kbd>
            </button>
            <DarkModeToggle />
            {user && <NotificationsBell />}
            {user ? (
              <>
                <Button onClick={goDashboard} data-testid="nav-dashboard-btn"
                  className="hidden md:inline-flex rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground">
                  <UserIcon className="w-4 h-4 mr-2" /> Dashboard
                </Button>
                <button onClick={logout} data-testid="nav-logout-btn" aria-label="Logout"
                  className="hidden md:flex w-10 h-10 rounded-full glass items-center justify-center hover:scale-110 transition-transform">
                  <LogOut className="w-4 h-4" strokeWidth={1.8} />
                </button>
              </>
            ) : (
              <Button onClick={triggerSignIn} data-testid="nav-signin-btn"
                className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 text-primary-foreground">
                <span className="hidden sm:inline">Sign in</span>
                <span className="sm:hidden">Sign in</span>
              </Button>
            )}
            <button className="lg:hidden w-10 h-10 rounded-full glass flex items-center justify-center" onClick={() => setMenuOpen(!menuOpen)} data-testid="menu-toggle">
              {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="lg:hidden glass-strong border-t border-border px-6 py-4 space-y-2">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)}
                className="block py-2 text-sm font-medium">
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </header>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
